<!--
  Manual scoreboard. Overlays on Record while recording and on SwingDetail.
  Two buttons "+1 P1" / "+1 P2", undo and reset. Classic tennis scoring.

  The parent owns the `state` prop (two-way-bound via `$bindable`) so the
  same instance can be persisted to the session record or lifted up.
-->
<script lang="ts">
  import type { Player } from '@tbc/core';
  import {
    awardPoint,
    emptyMatchState,
    pointLabel,
    resetMatch,
    undoPoint,
    type MatchState,
  } from './matchState.js';

  interface Props {
    state?: MatchState;
    players?: readonly Player[];
    compact?: boolean;
  }

  let { state = $bindable(emptyMatchState()), players = [], compact = false }: Props = $props();

  const p1Color = $derived(players.find((p) => p.id === 'p1')?.color ?? '#22c55e');
  const p2Color = $derived(players.find((p) => p.id === 'p2')?.color ?? '#06b6d4');
  const p1Name = $derived(players.find((p) => p.id === 'p1')?.name ?? 'P1');
  const p2Name = $derived(players.find((p) => p.id === 'p2')?.name ?? 'P2');

  const p1Label = $derived(pointLabel(state.p1Points, state.p2Points));
  const p2Label = $derived(pointLabel(state.p2Points, state.p1Points));

  const p1Games = $derived(state.games.filter((g) => g.winner === 'p1').length);
  const p2Games = $derived(state.games.filter((g) => g.winner === 'p2').length);

  function plusP1() {
    state = awardPoint(state, 'p1').state;
  }
  function plusP2() {
    state = awardPoint(state, 'p2').state;
  }
  function undo() {
    state = undoPoint(state);
  }
  function reset() {
    state = resetMatch();
  }
</script>

<div class="scoreboard" class:compact>
  <div class="rows">
    <div class="row" style:--accent={p1Color}>
      <div class="name">{p1Name}</div>
      <div class="games mono">{p1Games}</div>
      <div class="points mono">{p1Label}</div>
      <button type="button" class="add" aria-label="Adicionar ponto para {p1Name}" onclick={plusP1}>+1</button>
    </div>
    <div class="row" style:--accent={p2Color}>
      <div class="name">{p2Name}</div>
      <div class="games mono">{p2Games}</div>
      <div class="points mono">{p2Label}</div>
      <button type="button" class="add" aria-label="Adicionar ponto para {p2Name}" onclick={plusP2}>+1</button>
    </div>
  </div>
  <div class="actions">
    <button type="button" class="ghost" onclick={undo} disabled={state.history.length === 0}>Desfazer</button>
    <button type="button" class="ghost" onclick={reset}>Zerar</button>
  </div>
</div>

<style>
  .scoreboard {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 12px;
    background: rgba(15, 23, 42, 0.82);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(148, 163, 184, 0.25);
    border-radius: 12px;
    color: #e2e8f0;
    font-family: system-ui, sans-serif;
    min-width: 220px;
  }
  .mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }

  .rows { display: flex; flex-direction: column; gap: 4px; }
  .row {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-radius: 8px;
    border-left: 3px solid var(--accent);
    background: rgba(30, 41, 59, 0.6);
  }
  .name { font-weight: 600; font-size: 14px; color: var(--accent); }
  .games { font-size: 12px; opacity: 0.8; min-width: 14px; text-align: right; }
  .points { font-weight: 700; font-size: 20px; min-width: 36px; text-align: right; }
  .add {
    background: var(--accent);
    color: #0f172a;
    font-weight: 700;
    border: none;
    border-radius: 6px;
    padding: 6px 12px;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
  }
  .add:active { transform: scale(0.96); }

  .actions { display: flex; gap: 6px; justify-content: flex-end; }
  .ghost {
    background: transparent;
    color: #cbd5e1;
    border: 1px solid rgba(148, 163, 184, 0.25);
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    font-family: inherit;
  }
  .ghost:hover { color: #fff; border-color: rgba(148, 163, 184, 0.45); }
  .ghost:disabled { opacity: 0.4; cursor: not-allowed; }

  .compact .row { padding: 4px 6px; }
  .compact .name { font-size: 12px; }
  .compact .points { font-size: 16px; }
</style>
